import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Sparkles, MessageCircle, Instagram, Link2 } from "lucide-react";
import { API } from "@/config";
import PostEditorDialog from "@/components/admin/PostEditorDialog";
import { copyText } from "@/lib/clipboard";

export default function NewsPanel({ headers }) {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const media = (url) => (url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

  const load = () =>
    axios
      .get(`${API}/admin/posts`, { headers })
      .then((r) => setPosts(r.data))
      .catch(() => toast.error("No se pudieron cargar las novedades"));

  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (p) => {
    try {
      const res = await axios.put(`${API}/admin/posts/${p.id}`, { ...p, published: !p.published }, { headers });
      setPosts((prev) => prev.map((x) => (x.id === p.id ? res.data : x)));
      toast.success(res.data.published ? "Publicado en la web" : "Retirado de la web");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar «${p.title}»?`)) return;
    try {
      await axios.delete(`${API}/admin/posts/${p.id}`, { headers });
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Artículo eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const onSaved = (p) => setPosts((prev) => (prev.some((x) => x.id === p.id) ? prev.map((x) => (x.id === p.id ? p : x)) : [p, ...prev]));

  return (
    <section data-testid="admin-news-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Tendencias y novedades</h1>
          <p className="text-sm text-taupe mt-1">
            Cuéntale una idea a la IA o súbele fotos de tus trabajos y te escribe el artículo. Después lo editas, lo publicas o lo retiras cuando quieras.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="new-post-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-obsidian hover:bg-cocoa text-white text-sm font-medium px-6 py-3 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nuevo artículo
        </button>
      </div>

      {posts === null ? (
        <div className="mt-6 h-40 rounded-2xl bg-sand/50 animate-pulse" />
      ) : posts.length === 0 ? (
        <div data-testid="news-empty" className="mt-6 rounded-3xl bg-creamcard border border-sand p-10 text-center">
          <Sparkles className="w-8 h-8 text-gold mx-auto" />
          <p className="font-display text-xl text-obsidian mt-4">Todavía no hay novedades</p>
          <p className="text-sm text-taupe mt-1">Crea tu primer artículo y aparecerá en la página principal.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4">
          {posts.map((p) => (
            <article key={p.id} data-testid={`post-row-${p.id}`} className="rounded-2xl bg-creamcard border border-sand p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
              {p.image_url ? (
                <img src={media(p.image_url)} alt="" className="w-full sm:w-28 h-28 object-cover rounded-xl border border-sand shrink-0" />
              ) : (
                <div className="w-full sm:w-28 h-28 rounded-xl bg-sand/60 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-testid={`post-status-${p.id}`}
                    className={`rounded-full text-xs font-medium px-3 py-1 ${p.published ? "bg-emerald-100 text-emerald-800" : "bg-sand text-taupe"}`}
                  >
                    {p.published ? "publicado" : "borrador"}
                  </span>
                  {p.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="rounded-full bg-rosesoft text-cocoa text-[11px] px-2.5 py-0.5">{t}</span>
                  ))}
                </div>
                <h2 className="font-display text-lg sm:text-xl text-obsidian mt-2 truncate">{p.title}</h2>
                <p className="text-sm text-taupe mt-1 line-clamp-2">{p.excerpt}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`post-edit-btn-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => togglePublish(p)} data-testid={`post-publish-btn-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors">
                    {p.published ? <><EyeOff className="w-3.5 h-3.5" /> Despublicar</> : <><Eye className="w-3.5 h-3.5" /> Publicar</>}
                  </button>
                  {p.published && (
                    <a href={`/novedades/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`post-view-btn-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> Ver en la web
                    </a>
                  )}
                  <button onClick={() => remove(p)} data-testid={`post-delete-btn-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-red-600 hover:border-red-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                  </button>
                </div>
                {p.published && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-taupe">Compartir:</span>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`${p.title} — nueva novedad en ISI'S Nails 💅 ${window.location.origin}/novedades/${p.slug}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`post-share-whatsapp-${p.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                    <button
                      onClick={async () => {
                        await copyText(`${p.instagram || p.excerpt || p.title}\n\n${window.location.origin}/novedades/${p.slug}`);
                        toast.success("Texto y enlace copiados para Instagram");
                        window.open("https://www.instagram.com/", "_blank", "noopener");
                      }}
                      data-testid={`post-share-instagram-${p.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors"
                    >
                      <Instagram className="w-3.5 h-3.5" /> Instagram
                    </button>
                    <button
                      onClick={async () => { await copyText(`${window.location.origin}/novedades/${p.slug}`); toast.success("Enlace copiado"); }}
                      data-testid={`post-share-copy-${p.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand px-4 py-1.5 text-xs text-cocoa hover:border-gold hover:text-gold transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Copiar enlace
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <PostEditorDialog post={editing} open={open} onClose={() => setOpen(false)} onSaved={onSaved} headers={headers} />
    </section>
  );
}
